import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Package, Plus, ShoppingCart, ClipboardList, RefreshCw, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_SALE_LINES = 10;

const blankSaleLine = () => ({
  part_description: '',
  quantity: 1,
  unit_price: '',
  master_code: '',
  sku: '',
  product_id: '',
  _search: '',
  _open: false,
});

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(Number(n || 0));

/**
 * Clerk Sales Ring-Up.
 *
 * Feature-parity with the admin ADD SALE dialog for the ring-up itself
 * (multi-line ticket, per-line catalog search combobox, coloured payment
 * button-row, grand total, up to 10 lines). No other admin surfaces are
 * exposed — no cash-up, credits, salesmen management, catalog editing, etc.
 * View below is capped at sales from the last 3 calendar days.
 *
 * Requires clerk permission `can_ring_up_spares_sales` on the user record.
 */
export default function ClerkSalesRingUp() {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [busy, setBusy] = useState(false);

  const [customerName, setCustomerName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [saleLines, setSaleLines] = useState([blankSaleLine()]);

  // Salesman attribution + split commission (admin-parity)
  const [salesmen, setSalesmen] = useState([]);
  const [saleSalesmanId, setSaleSalesmanId] = useState('');
  const [splitOn, setSplitOn] = useState(false);
  const [splitSalesmen, setSplitSalesmen] = useState(['']);

  const [catalogParts, setCatalogParts] = useState([]);
  const [recent, setRecent] = useState({ window_start: '', count: 0, sales: [] });
  const [loading, setLoading] = useState(true);

  const fetchRecent = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/auto-spares/sales/recent-3days`, { headers });
      setRecent(r.data || { window_start: '', count: 0, sales: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load recent sales');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchRecent();
    // Autocomplete source is the LIVE inventory (auto_spares_products) — new
    // codes from the 2026-02 JSM price-list import land here immediately.
    axios
      .get(`${API_URL}/api/auto-spares/products`, { headers })
      .then((r) => setCatalogParts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCatalogParts([]));
    // Salesmen list — required for Select Salesman + Split Commission pickers.
    axios
      .get(`${API_URL}/api/auto-spares/admin/salesmen`, { headers })
      .then((r) => setSalesmen(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSalesmen([]));
  }, [fetchRecent, headers]);

  const updateSaleLine = (idx, patch) =>
    setSaleLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const addSaleLine = () =>
    setSaleLines((prev) => (prev.length >= MAX_SALE_LINES ? prev : [...prev, blankSaleLine()]));
  const removeSaleLine = (idx) =>
    setSaleLines((prev) => (prev.length <= 1 ? [blankSaleLine()] : prev.filter((_, i) => i !== idx)));

  const totalNewSale = saleLines.reduce(
    (s, l) => s + (parseFloat(l.unit_price) || 0) * (parseInt(l.quantity, 10) || 1),
    0,
  );

  const handleSaveSale = async () => {
    const cust = customerName.trim();
    if (!cust) return toast.error('Customer name is required');
    if (!saleSalesmanId) return toast.error('Select a salesman for this sale');
    const cleaned = saleLines
      .map((l) => ({
        part_description: (l.part_description || '').trim(),
        quantity: parseInt(l.quantity, 10) || 1,
        unit_price: parseFloat(l.unit_price) || 0,
        product_id: l.product_id || '',
        master_code: l.master_code || '',
        sku: l.sku || '',
      }))
      .filter((l) => l.part_description && l.unit_price > 0);
    if (!cleaned.length) return toast.error('Add at least one line (part description + price)');

    // Build commission_split payload (admin path) — equal split across primary + selected co-salesmen.
    let commissionSplit;
    if (splitOn) {
      const coIds = splitSalesmen.filter(Boolean);
      if (!coIds.length) return toast.error('Pick at least one co-salesman for the split');
      const dupCheck = new Set([saleSalesmanId, ...coIds]);
      if (dupCheck.size !== 1 + coIds.length) return toast.error('Duplicate salesman in split');
      commissionSplit = [saleSalesmanId, ...coIds].map((sid) => ({ salesman_id: sid }));
    }

    setBusy(true);
    try {
      await Promise.all(
        cleaned.map((line) =>
          axios.post(
            `${API_URL}/api/auto-spares/admin/sales`,
            {
              salesman_id: saleSalesmanId,
              part_description: line.part_description,
              quantity: line.quantity,
              unit_price: line.unit_price,
              product_id: line.product_id,
              master_code: line.master_code,
              sku: line.sku,
              payment_method: paymentMethod,
              customer_name: cust,
              ...(commissionSplit ? { commission_split: commissionSplit } : {}),
            },
            { headers },
          ),
        ),
      );
      toast.success(cleaned.length === 1 ? 'Sale saved ✓' : `${cleaned.length} lines saved ✓`);
      setCustomerName('');
      setPaymentMethod('cash');
      setSaleLines([blankSaleLine()]);
      setSaleSalesmanId('');
      setSplitOn(false);
      setSplitSalesmen(['']);
      fetchRecent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="clerk-ringup-page">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2 text-white">
            <ShoppingCart className="h-7 w-7 text-emerald-400" /> Auto Spares — Ring Up
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Book a walk-in sale. View below is limited to sales from the last 3 days.
          </p>
        </div>

        {/* ADD SALE card — identical layout to the admin dialog */}
        <div className="bg-slate-800 border border-slate-700 text-white rounded-lg p-5">
          <div className="flex items-center gap-2 mb-4">
            <Package className="h-5 w-5" />
            <h2 className="text-lg font-bold">ADD SALE</h2>
            <span className="text-xs text-slate-400 font-normal">(up to {MAX_SALE_LINES} lines)</span>
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-slate-300">Select Salesman *</Label>
              <Select value={saleSalesmanId} onValueChange={setSaleSalesmanId}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="ringup-salesman">
                  <SelectValue placeholder="Choose salesman..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  {salesmen.length === 0 ? (
                    <SelectItem value="__none" disabled>No salesmen configured</SelectItem>
                  ) : (
                    salesmen.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-300">Customer Name *</Label>
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="e.g., John Smith, Walk-in, ABC Motors"
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="ringup-customer"
              />
            </div>

            {saleLines.map((line, idx) => (
              <div
                key={idx}
                className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2"
                data-testid={`ringup-line-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-400">Line {idx + 1}</span>
                  {saleLines.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSaleLine(idx)}
                      className="text-rose-400 hover:text-rose-300 text-xs"
                      data-testid={`ringup-line-remove-${idx}`}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="relative">
                  <Label className="text-xs text-slate-300">Search Catalog</Label>
                  <Input
                    value={line._search || ''}
                    onFocus={() => updateSaleLine(idx, { _open: true })}
                    onChange={(e) => updateSaleLine(idx, { _search: e.target.value, _open: true })}
                    placeholder="Type 'BOD007', 'Door', 'Alternator', 'BRK', etc."
                    className="bg-slate-700 border-slate-600 text-white h-9"
                    data-testid={`ringup-catalog-search-${idx}`}
                    autoComplete="off"
                  />
                  {line._open && (line._search || '').trim().length > 0 && (() => {
                    const q = (line._search || '').trim().toLowerCase();
                    const matches = catalogParts.filter((p) =>
                      (p.supplier_sku || p.sku || p.master_code || '').toLowerCase().includes(q) ||
                      (p.name || '').toLowerCase().includes(q) ||
                      (p.category || '').toLowerCase().includes(q),
                    ).slice(0, 30);
                    return (
                      <div className="absolute top-full left-0 right-0 z-30 mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                        {matches.length === 0 ? (
                          <div className="px-3 py-3 text-slate-400 text-sm text-center">
                            No matches — type the description below manually.
                          </div>
                        ) : (
                          matches.map((p) => {
                            const code = p.supplier_sku || p.master_code || p.sku || '';
                            const price = p.selling_price ?? p.sale_price ?? 0;
                            return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() =>
                                updateSaleLine(idx, {
                                  part_description: `${code} — ${p.name}`,
                                  master_code: code,
                                  sku: code,
                                  unit_price: price ? String(price) : '',
                                  product_id: p.id || '',
                                  _search: `${code} — ${p.name}`,
                                  _open: false,
                                })
                              }
                              className="w-full text-left px-3 py-2 hover:bg-slate-700 flex items-center gap-2 border-b border-slate-700 last:border-0"
                              data-testid={`ringup-catalog-hit-${idx}-${p.id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="font-mono text-[11px] text-amber-300 font-bold">
                                  {code}
                                </div>
                                <div className="text-sm text-white truncate">{p.name}</div>
                                <div className="text-[10px] text-slate-500">
                                  {p.category}
                                  {p.subcategory ? ` • ${p.subcategory}` : ''}
                                  {typeof p.quantity === 'number' ? ` • stock ${p.quantity}` : ''}
                                </div>
                              </div>
                              <div className="text-emerald-400 font-bold text-sm shrink-0">
                                {formatCurrency(price)}
                              </div>
                            </button>
                            );
                          })
                        )}
                      </div>
                    );
                  })()}
                </div>

                <div>
                  <Label className="text-xs text-slate-300">Part Description *</Label>
                  <Input
                    value={line.part_description}
                    onChange={(e) => updateSaleLine(idx, { part_description: e.target.value })}
                    placeholder="e.g., Tail Light"
                    className="bg-slate-700 border-slate-600 text-white h-9"
                    data-testid={`ringup-part-${idx}`}
                  />
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs text-slate-300">Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      value={line.quantity}
                      onChange={(e) => updateSaleLine(idx, { quantity: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white h-9"
                      data-testid={`ringup-qty-${idx}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-300">Price (R) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={line.unit_price}
                      onChange={(e) => updateSaleLine(idx, { unit_price: e.target.value })}
                      placeholder="0.00"
                      className="bg-slate-700 border-slate-600 text-white h-9"
                      data-testid={`ringup-price-${idx}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-300">Line Total</Label>
                    <div className="h-9 px-3 flex items-center rounded-md bg-slate-700/60 border border-slate-600 text-emerald-400 font-semibold text-sm">
                      {formatCurrency(
                        (parseFloat(line.unit_price) || 0) * (parseInt(line.quantity, 10) || 1),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <Button
              type="button"
              onClick={addSaleLine}
              disabled={saleLines.length >= MAX_SALE_LINES}
              className="w-full bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white"
              data-testid="ringup-add-line"
            >
              <Plus className="h-4 w-4 mr-1" />
              {saleLines.length >= MAX_SALE_LINES
                ? `Max ${MAX_SALE_LINES} lines reached`
                : `Add another line (${saleLines.length}/${MAX_SALE_LINES})`}
            </Button>

            {/* Split Commission — same as admin dialog */}
            <div className="rounded-lg border border-fuchsia-700/40 bg-fuchsia-950/20 p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={splitOn}
                  onChange={(e) => { setSplitOn(e.target.checked); if (!e.target.checked) setSplitSalesmen(['']); }}
                  className="mt-1 h-4 w-4 accent-fuchsia-500"
                  data-testid="ringup-split-toggle"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white flex items-center gap-1">
                    <Users className="h-3 w-3 text-fuchsia-400" /> Split Commission
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Equal split among everyone. Sale stays on primary's history; co-salesmen share commission only.
                  </p>
                </div>
              </label>
              {splitOn && (
                <div className="mt-3 space-y-2" data-testid="ringup-split-area">
                  {splitSalesmen.map((cid, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-xs text-fuchsia-300 font-bold w-20">Co {idx + 1}</span>
                      <Select
                        value={cid}
                        onValueChange={(v) => setSplitSalesmen((prev) => prev.map((x, i) => (i === idx ? v : x)))}
                      >
                        <SelectTrigger className="bg-slate-700 border-slate-600 flex-1" data-testid={`ringup-split-co-${idx}`}>
                          <SelectValue placeholder="Choose co-salesman…" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-600">
                          {salesmen.filter((sm) => sm.id !== saleSalesmanId).map((sm) => (
                            <SelectItem key={sm.id} value={sm.id}>{sm.full_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {splitSalesmen.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setSplitSalesmen((prev) => prev.filter((_, i) => i !== idx))}
                          className="text-rose-400 hover:text-rose-300 text-xs"
                          data-testid={`ringup-split-remove-${idx}`}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  {splitSalesmen.length < 2 && (
                    <Button
                      type="button"
                      onClick={() => setSplitSalesmen((prev) => [...prev, ''])}
                      className="w-full bg-fuchsia-700/40 hover:bg-fuchsia-700/60 text-fuchsia-200 text-xs"
                      data-testid="ringup-split-add"
                    >
                      + Add another co-salesman (max 3 total)
                    </Button>
                  )}
                  <p className="text-[11px] text-fuchsia-300 mt-1">
                    Split will be equal: <strong>{(100 / (1 + splitSalesmen.filter(Boolean).length)).toFixed(2)}%</strong> each.
                  </p>
                </div>
              )}
            </div>

            <div>
              <Label className="text-slate-300">Payment Method *</Label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {[
                  { v: 'cash',   label: 'Cash',   cls: 'bg-emerald-600 hover:bg-emerald-700 border-emerald-500' },
                  { v: 'online', label: 'Online', cls: 'bg-blue-600 hover:bg-blue-700 border-blue-500' },
                  { v: 'eft',    label: 'EFT',    cls: 'bg-purple-600 hover:bg-purple-700 border-purple-500' },
                  { v: 'card',   label: 'Card',   cls: 'bg-amber-600 hover:bg-amber-700 border-amber-500' },
                  { v: 'credit', label: 'Credit', cls: 'bg-rose-600 hover:bg-rose-700 border-rose-500' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setPaymentMethod(opt.v)}
                    data-testid={`ringup-pm-${opt.v}`}
                    className={`rounded-md border px-2 py-2 text-sm font-semibold text-white transition ${
                      paymentMethod === opt.v
                        ? opt.cls + ' ring-2 ring-white/40'
                        : 'bg-slate-700 border-slate-600 hover:bg-slate-600'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-slate-700 p-3 rounded-lg flex items-center justify-between">
              <p className="text-sm text-slate-400">
                GRAND TOTAL ({saleLines.length} {saleLines.length === 1 ? 'line' : 'lines'}):
              </p>
              <p className="text-2xl font-bold text-emerald-400" data-testid="ringup-total">
                {formatCurrency(totalNewSale)}
              </p>
            </div>

            <Button
              onClick={handleSaveSale}
              disabled={busy}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="ringup-submit"
            >
              {busy ? 'Saving…' : 'SAVE SALE'}
            </Button>
          </div>
        </div>

        {/* Past 3 days */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-blue-400" />
              Past 3 days of sales
              <Badge variant="outline" className="ml-2 bg-slate-700 text-slate-200 border-slate-600">
                {recent.count} sale{recent.count === 1 ? '' : 's'} since {recent.window_start || '—'}
              </Badge>
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={fetchRecent} className="text-slate-300 hover:text-white" data-testid="ringup-refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-8 text-slate-400">Loading…</div>
            ) : recent.sales.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">No sales in the last 3 days.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Date</TableHead>
                      <TableHead className="text-slate-400">Customer</TableHead>
                      <TableHead className="text-slate-400">Part</TableHead>
                      <TableHead className="text-slate-400 text-center">Qty</TableHead>
                      <TableHead className="text-slate-400 text-right">Unit</TableHead>
                      <TableHead className="text-slate-400 text-right">Total</TableHead>
                      <TableHead className="text-slate-400">Payment</TableHead>
                      <TableHead className="text-slate-400">Booked by</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recent.sales.map((s) => (
                      <TableRow key={s.id} className="border-slate-700" data-testid={`recent-sale-${s.id}`}>
                        <TableCell className="text-slate-300 text-xs font-mono">{s.date}</TableCell>
                        <TableCell className="text-cyan-300 text-sm">{s.customer_name || '—'}</TableCell>
                        <TableCell className="text-white text-sm max-w-xs truncate">{s.part_description}</TableCell>
                        <TableCell className="text-white text-center">{s.quantity}</TableCell>
                        <TableCell className="text-white text-right">{formatCurrency(s.unit_price)}</TableCell>
                        <TableCell className="text-emerald-400 text-right font-bold">{formatCurrency(s.total)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize bg-slate-700 text-slate-200 border-slate-600">
                            {s.payment_method || 'cash'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-amber-300 text-xs">
                          {s.salesman_name || '—'}
                          {s.booked_by_role === 'clerk' && (
                            <Badge variant="outline" className="ml-1 bg-indigo-900/40 text-indigo-300 border-indigo-700 text-[10px]">
                              clerk
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
